/**
 * Bullet tracers (67.15): one draw for every streak in flight, each a camera-facing ribbon a few pixels wide on
 * screen — bright and hot at the head, tapering and fading to the tail — drawn additive so it reads against sky,
 * fog and ground alike. The host fills it each frame (`begin`, `add` per streak, `end`); presentation only.
 *
 * It replaces a 1-pixel line of fixed length: at 1.5–2× bullet speed (the Lab's Bullet speed row) a round moves
 * further between two frames than that line was long, so it read as a dash jumping in gaps, and thin at any range.
 */
import * as THREE from 'three';
import type { Vec3 } from '../types';

const VERT = /* glsl */ `
attribute vec3 aHead;
attribute vec3 aTail;
attribute vec2 aCorner;   // x: 0 tail, 1 head; y: -1 / +1 across
attribute float aAlpha;
uniform vec2 uViewport;    // the drawing buffer, px
uniform float uWidth;      // the head's full width, px
varying float vAlong;
varying float vSide;
varying float vAlpha;
void main() {
  vec4 h = projectionMatrix * modelViewMatrix * vec4(aHead, 1.0);
  vec4 t = projectionMatrix * modelViewMatrix * vec4(aTail, 1.0);
  if (t.w < 1e-4) t = h;   // a tail behind the camera: draw the head alone
  if (h.w < 1e-4) h = t;
  vec2 hs = h.xy / h.w * uViewport * 0.5;
  vec2 ts = t.xy / t.w * uViewport * 0.5;
  vec2 dir = hs - ts;
  float l = length(dir);
  dir = l > 1e-3 ? dir / l : vec2(1.0, 0.0);
  vec2 across = vec2(-dir.y, dir.x);
  bool head = aCorner.x > 0.5;
  vec4 p = head ? h : t;
  float w = head ? uWidth * 0.5 : uWidth * 0.2;
  vec2 off = across * aCorner.y * w + (head ? dir * w : vec2(0.0));   // a rounded-off head a half-width beyond the point
  p.xy += off / (uViewport * 0.5) * p.w;
  gl_Position = p;
  vAlong = aCorner.x;
  vSide = aCorner.y;
  vAlpha = aAlpha;
}`;

const FRAG = /* glsl */ `
uniform vec3 uColor;
uniform vec3 uHot;
varying float vAlong;
varying float vSide;
varying float vAlpha;
void main() {
  float edge = smoothstep(0.0, 0.7, 1.0 - abs(vSide));
  float a = pow(vAlong, 1.4) * edge * vAlpha * 0.85;
  gl_FragColor = vec4(mix(uColor, uHot, vAlong * vAlong), a);
}`;

export class Tracers {
  private readonly mesh: THREE.Mesh;
  private readonly head: Float32Array;
  private readonly tail: Float32Array;
  private readonly alpha: Float32Array;
  private readonly mat: THREE.ShaderMaterial;
  private count = 0;

  constructor(scene: THREE.Scene, private readonly max = 512) {
    const v = max * 4;
    this.head = new Float32Array(v * 3);
    this.tail = new Float32Array(v * 3);
    this.alpha = new Float32Array(v);
    const corner = new Float32Array(v * 2);
    const index = new Uint32Array(max * 6);
    for (let i = 0; i < max; i++) {
      corner.set([0, -1, 0, 1, 1, -1, 1, 1], i * 8);
      const b = i * 4;
      index.set([b, b + 2, b + 1, b + 1, b + 2, b + 3], i * 6);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('aHead', new THREE.BufferAttribute(this.head, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aTail', new THREE.BufferAttribute(this.tail, 3).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aAlpha', new THREE.BufferAttribute(this.alpha, 1).setUsage(THREE.DynamicDrawUsage));
    geo.setAttribute('aCorner', new THREE.BufferAttribute(corner, 2));
    // three needs a position attribute to draw; the shader never reads it
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(v * 3), 3));
    geo.setIndex(new THREE.BufferAttribute(index, 1));
    geo.setDrawRange(0, 0);
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false, depthTest: true,
      blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      uniforms: { uViewport: { value: new THREE.Vector2(1, 1) }, uWidth: { value: 3 }, uColor: { value: new THREE.Color(0xffb04a) }, uHot: { value: new THREE.Color(0xffeac0) } },
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 10;
    scene.add(this.mesh);
  }

  /** Start a frame: the drawing buffer's size (px) and the head's width in CSS px (× the device pixel ratio). */
  begin(bufferWidth: number, bufferHeight: number, pixelRatio: number): void {
    this.count = 0;
    (this.mat.uniforms.uViewport.value as THREE.Vector2).set(bufferWidth, bufferHeight);
    this.mat.uniforms.uWidth.value = 2.4 * pixelRatio;   // owner, Oct 10: 3 was "slightly slightly over the top"
  }

  /** One streak from `tail` to `head`, at `alpha` (0–1). */
  add(head: Vec3, tail: Vec3, alpha = 1): void {
    if (this.count >= this.max) return;
    const i = this.count++;
    for (let c = 0; c < 4; c++) {
      const o = (i * 4 + c) * 3;
      this.head[o] = head.x; this.head[o + 1] = head.y; this.head[o + 2] = head.z;
      this.tail[o] = tail.x; this.tail[o + 1] = tail.y; this.tail[o + 2] = tail.z;
      this.alpha[i * 4 + c] = alpha;
    }
  }

  end(): void {
    const geo = this.mesh.geometry;
    geo.setDrawRange(0, this.count * 6);
    for (const k of ['aHead', 'aTail', 'aAlpha']) {
      const a = geo.getAttribute(k) as THREE.BufferAttribute;
      a.clearUpdateRanges();
      a.addUpdateRange(0, this.count * 4 * a.itemSize);
      a.needsUpdate = true;
    }
  }

  dispose(): void {
    this.mesh.parent?.remove(this.mesh);
    this.mesh.geometry.dispose();
    this.mat.dispose();
  }
}
